/* eslint-disable camelcase */
import { FC, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { platform } from 'network-utils';
import { useDispatch, useSelector } from 'react-redux';
import { LearningPanelDialog, ProfilePictureActions, ProfilePictureInnerActions } from 'learning-panel-components';
import { translate } from 'translations';
import { selectUserUuid } from '@redux/slices/userSlice';
import { showAlert } from '@redux/slices/informationSlice';
import { useGetAccountData, getAccountData } from '@queries/platform/accountSettings';
import { getUserProfilePictureKey } from '@queries/platform/aws';
import ProfilePicture from './profilePicture';

const ProfilePictureContainer: FC<ProfilePictureContainerProps> = (props: ProfilePictureContainerProps) => {
    const { setShowSuccessNotification, setNotificationMessage, setIsLoading, isLoading } = props;
    const services = platform.learningPanel.AccountSettingsAPIs;
    const queryClient = useQueryClient();
    const userUuid = useSelector(selectUserUuid);
    const dispatch = useDispatch();
    const { data, isLoading: isLoadingAccountData } = useGetAccountData(userUuid);
    const accountData = data?.account_data;

    const failed = translate('generalError');
    const deleteProfilePictureText = translate('ProfilePictureDeleted');
    const replaceProfilePictureText = translate('ProfilePictureReplaced');
    const replaceImageText = translate('ReplaceImage');
    const removeImageText = translate('RemoveImage');

    const [fileContent, setFileContent] = useState<File>();
    const [fileName, setFileName] = useState<string>('');
    const [showDeleteProfilePictureDialog, setShowDeleteProfilePictureDialog] = useState<boolean>(false);
    const [profilePictureContent, setProfilePictureContent] = useState<File>();

    const onDeleteProfilePicture = () => {
        setShowDeleteProfilePictureDialog(false);
        deleteProfilePictureMutation.mutate();
    };

    const actions: ProfilePictureActions[] = [
        {
            title: replaceImageText,
            icon: 'replace',
            actionKey: ProfilePictureInnerActions.REPLACE
        },
        {
            title: removeImageText,
            icon: 'deleteIcon',
            action: setShowDeleteProfilePictureDialog,
            actionKey: ProfilePictureInnerActions.DELETE
        }
    ];

    const deleteProfilePicture = async (): Promise<any> => {
        const deleteProfilePicResponse = await services.deleteProfilePicture();
        return deleteProfilePicResponse;
    };

    const replaceProfilePicture = async (filename: string): Promise<any> => {
        const generateUploadUrlResponse = await services.getUploadProfileUrlWithFilename(filename);
        let uploadResponse = new Response();
        if (fileContent) {
            uploadResponse = await services.uploadImage(generateUploadUrlResponse.url, fileContent, fileContent.type);
            await services.setProfilePicture({ profile_picture_filename: generateUploadUrlResponse?.new_filename });
        }

        return { generateUploadUrlResponse, uploadResponse };
    };

    const generalError = () => {
        dispatch(
            showAlert({
                value: failed,
                status: 'error'
            })
        );
    };

    const deleteProfilePictureMutation = useMutation(deleteProfilePicture, {
        onSuccess: () => {
            queryClient.setQueryData([getAccountData, userUuid], oldData => ({
                account_data: {
                    ...(oldData as platform.AccountData).account_data,
                    profile_picture_url: ''
                }
            }));
            queryClient.invalidateQueries([getUserProfilePictureKey, userUuid]);
            setProfilePictureContent(undefined);
            setShowSuccessNotification?.(true);
            setNotificationMessage?.(deleteProfilePictureText);
        },
        onError: () => {
            generalError();
        }
    });

    const replaceProfilePictureMutation = useMutation(replaceProfilePicture, {
        onSuccess: () => {
            queryClient.invalidateQueries([getAccountData, userUuid]);
            queryClient.invalidateQueries([getUserProfilePictureKey, userUuid]);
            setShowSuccessNotification?.(true);
            setNotificationMessage?.(replaceProfilePictureText);
            setProfilePictureContent(fileContent);
        },
        onError: () => {
            generalError();
        }
    });

    useEffect(() => {
        setIsLoading?.(isLoadingAccountData);
    }, [isLoadingAccountData]);

    useEffect(() => {
        if (fileName && fileContent) {
            replaceProfilePictureMutation.mutate(fileName);
        }
    }, [fileName, fileContent]);

    return (
        <>
            {isLoading ? undefined : (
                <>
                    <ProfilePicture
                        imgSrc={profilePictureContent}
                        imgSrcUrl={accountData?.profile_picture_url}
                        uploadImage={(fc: File, fn: string) => {
                            setFileContent(fc);
                            setFileName(fn);
                        }}
                        actions={profilePictureContent || accountData?.profile_picture_url ? actions : undefined}
                        isLoading={replaceProfilePictureMutation.isLoading || deleteProfilePictureMutation.isLoading}
                    />
                    <LearningPanelDialog
                        open={showDeleteProfilePictureDialog}
                        title={translate('AreYouSureYouWantToRemoveImage')}
                        content={translate('removeImageBody')}
                        onClose={() => setShowDeleteProfilePictureDialog(false)}
                        okButtonOnClick={onDeleteProfilePicture}
                        okButtonText={translate('RemoveButton')}
                        cancelButtonOnClick={() => setShowDeleteProfilePictureDialog(false)}
                        cancelButtonText={translate('Cancel')}
                    />
                </>
            )}
        </>
    );
};

export interface ProfilePictureContainerProps {
    setShowSuccessNotification?: Function;
    setNotificationMessage?: Function;
    setIsLoading?: Function;
    isLoading: boolean;
    marginLeft?: string;
}

export default ProfilePictureContainer;
